import type { InventoryVehicle } from '@carvana/merch-contracts';
import { useEffect, useMemo, useRef } from 'react';
import { useResponseErrors } from '~/context/srp/ErrorContext';
import {
  useInventoryBulkData,
  useUserDeliveryInfo
} from '~/context/srp/InventoryContext';
import getVehicleData from '../getVehicleData';

type OwnProps = {
  latestInventoryVehicles: InventoryVehicle[];
  latestSimilarVehicles: InventoryVehicle[];
  shouldHaveSimilarVehicles: boolean;
};

export default function useGetVehiclesLoaded({
  latestInventoryVehicles,
  latestSimilarVehicles,
  shouldHaveSimilarVehicles
}: OwnProps) {
  const { availabilityError, lowAvailabilityError, pricingError } =
    useResponseErrors();

  const userDeliveryInfo = useUserDeliveryInfo();
  const userDeliveryInfoRef = useRef(userDeliveryInfo);
  const inventoryBulkData = useInventoryBulkData();

  useEffect(() => {
    userDeliveryInfoRef.current = userDeliveryInfo;
  }, [userDeliveryInfo]);

  return useMemo(() => {
    const inventoryVehicles = getVehicleData(
      latestInventoryVehicles,
      inventoryBulkData
    );
    const hasPricing =
      Boolean(pricingError) ||
      inventoryVehicles.some(
        v => v.tileInfo.hasTerms || v.tileInfo.pricingErrorMessage
      );

    const hasAvailability =
      userDeliveryInfoRef.current?.isOutOfMarket === true ||
      Boolean(availabilityError) ||
      inventoryVehicles.some(v => Boolean(v.pickupInfo || v.deliveryInfo));

    const inventoryVehiclesLoaded =
      inventoryVehicles.length === 0 || (hasPricing && hasAvailability);

    const similarVehiclesToSend = shouldHaveSimilarVehicles
      ? getVehicleData(latestSimilarVehicles, inventoryBulkData)
      : [];

    const similarVehiclesAvailability =
      Boolean(lowAvailabilityError) ||
      similarVehiclesToSend.some(v => Boolean(v.pickupInfo || v.deliveryInfo));

    const similarVehiclesLoaded =
      similarVehiclesToSend.length > 0 && similarVehiclesAvailability;

    return {
      loadedInventoryVehicles: {
        isLoaded: inventoryVehiclesLoaded,
        vehicles: inventoryVehicles
      },
      loadedSimilarVehicles: {
        isLoaded: similarVehiclesLoaded,
        vehicles: similarVehiclesToSend
      }
    };
  }, [
    availabilityError,
    inventoryBulkData,
    latestInventoryVehicles,
    latestSimilarVehicles,
    lowAvailabilityError,
    pricingError,
    shouldHaveSimilarVehicles
  ]);
}
